// ✅ src/pages/Asset/AssetHomeDashboard.jsx
// Backend mount:
//   app.use("/api/assetHome", assethome);
//
// APIs used:
//   GET  /api/assetHome/renewal-alerts
//   GET  /api/assetHome/calibration-alerts
//   GET  /api/assetHome/calibration-alerts/excel   (download)

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import AssetLayoutWrapper from "../components/AssetLayoutWrapper";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaBell,
  FaCalendarAlt,
  FaCar,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEnvelope,
  FaEye,
  FaFileExcel,
  FaPrint,
  FaRedo,
  FaSearch,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/assetHome";

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function VehicleCalibrationAlerts() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [loading, setLoading] = useState(false);
  const [taxAlerts, setTaxAlerts] = useState([]);
  const [calAlerts, setCalAlerts] = useState([]);

  const [taxSearch, setTaxSearch] = useState("");
  const [calSearch, setCalSearch] = useState("");

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : "bg-white border-gray-200 text-black"
  }`;

  const sectionClass = `rounded-xl border overflow-hidden ${
    isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
  }`;

  const th = `px-5 py-4 text-left font-semibold text-white whitespace-nowrap`;
  const td = `px-5 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-700"} whitespace-nowrap`;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [taxRes, calRes] = await Promise.all([
        axios.get(`${API_BASE}/renewal-alerts`, {
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/calibration-alerts`, {
          headers: commonHeaders,
        }),
      ]);

      if (taxRes.data?.success) setTaxAlerts(taxRes.data?.data || []);
      else toast.error(taxRes.data?.message || "Failed to load renewal alerts");

      if (calRes.data?.success) setCalAlerts(calRes.data?.data || []);
      else toast.error(calRes.data?.message || "Failed to load calibration alerts");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const filteredTax = useMemo(() => {
    const q = taxSearch.trim().toLowerCase();
    if (!q) return taxAlerts;

    return taxAlerts.filter((r) => {
      const a = toStr(r.fld_assettypename).toLowerCase();
      const c = toStr(r.fld_assetcode).toLowerCase();
      const t = toStr(r.fld_taxtype).toLowerCase();
      const loc = toStr(r.fld_location).toLowerCase();
      return a.includes(q) || c.includes(q) || t.includes(q) || loc.includes(q);
    });
  }, [taxAlerts, taxSearch]);

  const filteredCal = useMemo(() => {
    const q = calSearch.trim().toLowerCase();
    if (!q) return calAlerts;

    return calAlerts.filter((r) => {
      const a = toStr(r.fld_assettypename).toLowerCase();
      const c = toStr(r.fld_assetcode).toLowerCase();
      const reg = toStr(r.fld_description).toLowerCase();
      const st = toStr(r.Status).toLowerCase();
      return a.includes(q) || c.includes(q) || reg.includes(q) || st.includes(q);
    });
  }, [calAlerts, calSearch]);

  const stats = useMemo(() => {
    const taxTotal = taxAlerts.length;
    const taxDueSoon = taxAlerts.filter((x) =>
      toStr(x.fld_renewalalert).toLowerCase().includes("expire")
    ).length;

    const calTotal = calAlerts.length;
    const calExpireToday = calAlerts.filter((x) =>
      toStr(x.Status).toLowerCase().includes("today")
    ).length;

    const cal1to5 = calAlerts.filter((x) => {
      const s = toStr(x.Status).toLowerCase();
      return (
        s.includes("1 day left") ||
        s.includes("2 days left") ||
        s.includes("3 days left") ||
        s.includes("4 days left") ||
        s.includes("5 days left")
      );
    }).length;

    const cal6to15 = calAlerts.filter((x) => {
      const s = toStr(x.Status).toLowerCase();
      return (
        s.includes("days left") &&
        !(
          s.includes("1 day left") ||
          s.includes("2 days left") ||
          s.includes("3 days left") ||
          s.includes("4 days left") ||
          s.includes("5 days left")
        )
      );
    }).length;

    return { taxTotal, taxDueSoon, calTotal, calExpireToday, cal1to5, cal6to15 };
  }, [taxAlerts, calAlerts]);

  const calStatusBadge = (status) => {
    const s = toStr(status).toLowerCase();
    const base =
      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border";

    if (s.includes("expire today")) {
      return (
        <span className={`${base} bg-red-100 text-red-800 border-red-200`}>
          <FaExclamationTriangle className="text-red-500" />
          Expire Today
        </span>
      );
    }

    if (s.includes("day left") || s.includes("days left")) {
      return (
        <span className={`${base} bg-yellow-100 text-yellow-800 border-yellow-200`}>
          <FaClock className="text-yellow-600" />
          {toStr(status)}
        </span>
      );
    }

    return (
      <span className={`${base} bg-green-100 text-green-800 border-green-200`}>
        <FaCheckCircle className="text-green-600" />
        {toStr(status) || "Current"}
      </span>
    );
  };

  const handleSendSMS = (row) => {
    const det = [
      toStr(row.fld_assetcode),
      toStr(row.fld_location),
      toStr(row.fld_assettypename),
      toStr(row.fld_taxtype),
      toStr(row.fld_renewaldate),
    ].join(",");

    const pageName = `/SMS/DashboardSMS.aspx?Det=${encodeURIComponent(det)}`;
    window.open(
      pageName,
      "_blank",
      "width=400,height=150,left=(screen.width/2)-(400/2),top=(screen.height/2)-(250/2),scrollbars=yes,menubar=no,resizable=no,directories=no,location=no"
    );
  };

  // ✅ Excel download with headers
  const handleExcel = async () => {
    try {
      const response = await axios.get(`${API_BASE}/calibration-alerts/excel`, {
        headers: commonHeaders,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Vehicle Calibration Report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to export excel");
    }
  };

  const handlePrint = () => {
    window.open("/Asset/vehiclecalibratinalertreport", "_blank");
  };

  const handleViewAsset = (assetId) => {
    navigate(`/Asset/AssetMaster?assetid=${assetId}`);
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET HOME</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <FaBell className={`text-3xl ${isDark ? "text-blue-400" : "text-blue-600"}`} />
              <div>
                <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Alerts Dashboard
                </div>
                <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm`}>
                  Renewal alerts + vehicle calibration alerts
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={loadAll}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
                title="Refresh"
              >
                <FaRedo />
                Refresh
              </button>

              <button
                onClick={handleExcel}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark ? "bg-green-700 hover:bg-green-600 text-white" : "bg-green-600 hover:bg-green-700 text-white"
                }`}
                title="Export Excel"
              >
                <FaFileExcel />
                Excel
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
                title="Print"
              >
                <FaPrint />
                Print
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className={`p-5 rounded-xl border ${isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-medium`}>Tax Alerts</div>
                  <div className={`${isDark ? "text-white" : "text-gray-900"} text-3xl font-bold`}>{stats.taxTotal}</div>
                </div>
                <FaCalendarAlt className="text-3xl text-blue-500" />
              </div>
            </div>

            <div className={`p-5 rounded-xl border ${isDark ? "bg-gray-800 border-gray-700" : "bg-yellow-50 border-yellow-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-medium`}>Due within 30 days</div>
                  <div className="text-3xl font-bold text-yellow-600">{stats.taxDueSoon}</div>
                </div>
                <FaClock className="text-3xl text-yellow-500" />
              </div>
            </div>

            <div className={`p-5 rounded-xl border ${isDark ? "bg-gray-800 border-gray-700" : "bg-red-50 border-red-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-medium`}>Expire Today</div>
                  <div className="text-3xl font-bold text-red-600">{stats.calExpireToday}</div>
                </div>
                <FaExclamationTriangle className="text-3xl text-red-500" />
              </div>
            </div>

            <div className={`p-5 rounded-xl border ${isDark ? "bg-gray-800 border-gray-700" : "bg-green-50 border-green-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-medium`}>Calibration Alerts</div>
                  <div className="text-3xl font-bold text-green-600">{stats.calTotal}</div>
                </div>
                <FaCar className="text-3xl text-green-500" />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <FaCalendarAlt className={`${isDark ? "text-blue-300" : "text-blue-600"}`} />
                <div className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Tax / Renewal Alerts
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative" style={{ maxWidth: 340, width: "100%" }}>
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={taxSearch}
                    onChange={(e) => setTaxSearch(e.target.value)}
                    className={`pl-10 ${inputClass}`}
                    placeholder="Search asset / code / tax / location..."
                  />
                </div>
                <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm`}>
                  {loading ? "Loading..." : `Showing ${filteredTax.length} of ${taxAlerts.length}`}
                </div>
              </div>
            </div>

            <div className={sectionClass}>
              {loading ? (
                <div className={`p-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              ) : filteredTax.length === 0 ? (
                <div className={`p-8 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className={th}>S.No</th>
                        <th className={th}>Class of Asset</th>
                        <th className={th}>Asset Code</th>
                        <th className={th}>Tax Type</th>
                        <th className={th}>Renewal Date</th>
                        <th className={th}>Renewal Alert</th>
                        <th className={`${th} text-center`}>SendSMS</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                      {filteredTax.map((r, idx) => (
                        <tr
                          key={`${r.fld_assetid}-${idx}`}
                          className={`transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={td}>{idx + 1}</td>
                          <td className={td}>{toStr(r.fld_assettypename)}</td>
                          <td className={td}>{toStr(r.fld_assetcode)}</td>
                          <td className={td}>{toStr(r.fld_taxtype)}</td>
                          <td className={td}>{toStr(r.fld_renewaldate)}</td>
                          <td className={`${td} font-semibold text-yellow-600`}>
                            {toStr(r.fld_renewalalert)}
                          </td>
                          <td className={`${td} text-center`}>
                            <button
                              onClick={() => handleSendSMS(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                isDark ? "bg-indigo-700 hover:bg-indigo-600 text-white" : "bg-indigo-100 hover:bg-indigo-200 text-indigo-800"
                              }`}
                            >
                              <FaEnvelope />
                              SendSMS
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <FaCar className={`${isDark ? "text-blue-300" : "text-blue-600"}`} />
                <div className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Vehicle Calibration Alerts
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative" style={{ maxWidth: 340, width: "100%" }}>
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={calSearch}
                    onChange={(e) => setCalSearch(e.target.value)}
                    className={`pl-10 ${inputClass}`}
                    placeholder="Search asset / code / reg no / status..."
                  />
                </div>
                <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm`}>
                  {loading ? "Loading..." : `Showing ${filteredCal.length} of ${calAlerts.length}`}
                </div>
              </div>
            </div>

            <div className={sectionClass}>
              {loading ? (
                <div className={`p-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              ) : filteredCal.length === 0 ? (
                <div className={`p-8 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className={th}>S.No</th>
                        <th className={th}>Asset Name</th>
                        <th className={th}>Asset Code</th>
                        <th className={th}>Vehical Reg.No</th>
                        <th className={th}>Next Calibration Date</th>
                        <th className={th}>Status</th>
                        <th className={`${th} text-center`}>View</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                      {filteredCal.map((r, idx) => (
                        <tr
                          key={`${r.fld_assetid}-${idx}`}
                          className={`transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={td}>{idx + 1}</td>
                          <td className={td}>{toStr(r.fld_assettypename)}</td>
                          <td className={td}>{toStr(r.fld_assetcode)}</td>
                          <td className={td}>{toStr(r.fld_description)}</td>
                          <td className={td}>{toStr(r.fld_nextcalibrationdate)}</td>
                          <td className={td}>{calStatusBadge(r.Status)}</td>
                          <td className={`${td} text-center`}>
                            <button
                              onClick={() => handleViewAsset(r.fld_assetid)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                isDark ? "bg-blue-700 hover:bg-blue-600 text-white" : "bg-blue-100 hover:bg-blue-200 text-blue-800"
                              }`}
                            >
                              <FaEye />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className={`mt-4 p-4 rounded-lg border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
                <div className={isDark ? "text-gray-300" : "text-gray-600"}>
                  Total: {calAlerts.length} | Expire Today: {stats.calExpireToday} | 1-5 Days: {stats.cal1to5} | 6-15 Days: {stats.cal6to15}
                </div>
                <div className={isDark ? "text-gray-300" : "text-gray-600"}>
                  Last updated: {new Date().toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </AssetLayoutWrapper>
  );
}